import React, { useState } from "react";
import { InterviewSession, WordStatus } from "../../types";

interface InterviewResultsProps {
  session: InterviewSession;
  onRestart: () => void;
  onGoHome: () => void;
}

const InterviewResults: React.FC<InterviewResultsProps> = ({
  session,
  onRestart,
  onGoHome,
}) => {
  const [expandedQuestion, setExpandedQuestion] = useState<number | null>(0);

  const questionsWithAnswers = session.questions.filter((q) => q.answer);

  const averagePronunciationScore =
    questionsWithAnswers.length > 0
      ? Math.round(
          questionsWithAnswers.reduce(
            (sum, q) => sum + (q.answer?.pronunciationScore || 0),
            0
          ) / questionsWithAnswers.length
        )
      : 0;

  const averageAnswerScore =
    questionsWithAnswers.length > 0
      ? Math.round(
          questionsWithAnswers.reduce(
            (sum, q) => sum + (q.answer?.evaluation?.score || 0),
            0
          ) / questionsWithAnswers.length
        )
      : 0;

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600 dark:text-green-400";
    if (score >= 50) return "text-yellow-600 dark:text-yellow-400";
    return "text-red-600 dark:text-red-400";
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return "bg-green-100 dark:bg-green-900/30";
    if (score >= 50) return "bg-yellow-100 dark:bg-yellow-900/30";
    return "bg-red-100 dark:bg-red-900/30";
  };

  const getWordStyles = (status: WordStatus): string => {
    switch (status) {
      case "correct":
        return "bg-green-200 dark:bg-green-800/50 text-green-800 dark:text-green-300";
      case "unclear":
        return "bg-yellow-200 dark:bg-yellow-800/50 text-yellow-800 dark:text-yellow-300";
      case "substitution":
        return "bg-red-200 dark:bg-red-800/50 text-red-800 dark:text-red-300";
      case "omission":
        return "bg-red-200 dark:bg-red-800/50 text-red-800 dark:text-red-300 opacity-60";
      case "insertion":
        return "bg-purple-200 dark:bg-purple-800/50 text-purple-800 dark:text-purple-300";
      default:
        return "bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-300";
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-8 text-green-600 dark:text-green-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
          Interview Complete!
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Here's how you did in your {session.jobTitle} interview practice
        </p>
      </div>

      {/* Overall scores */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Pronunciation</p>
          <p className={`text-4xl font-bold ${getScoreColor(averagePronunciationScore)}`}>
            {averagePronunciationScore}%
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Answer Quality</p>
          <p className={`text-4xl font-bold ${getScoreColor(averageAnswerScore * 10)}`}>
            {averageAnswerScore}/10
          </p>
        </div>
      </div>

      {/* Question by question breakdown */}
      <div className="space-y-4 mb-8">
        <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300">Detailed Results</h2>

        {session.questions.map((question, index) => {
          const isExpanded = expandedQuestion === index;
          const answer = question.answer;

          return (
            <div key={question.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
              {/* Question header */}
              <button
                onClick={() => setExpandedQuestion(isExpanded ? null : index)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <span className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center text-purple-600 dark:text-purple-400 font-bold text-sm">
                    {index + 1}
                  </span>
                  <span className="text-left text-gray-800 dark:text-gray-200 font-medium">
                    {question.question.length > 60
                      ? question.question.substring(0, 60) + "..."
                      : question.question}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  {answer && (
                    <>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${getScoreBgColor(
                          answer.pronunciationScore
                        )} ${getScoreColor(answer.pronunciationScore)}`}
                      >
                        {answer.pronunciationScore}%
                      </span>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${getScoreBgColor(
                          (answer.evaluation?.score || 0) * 10
                        )} ${getScoreColor((answer.evaluation?.score || 0) * 10)}`}
                      >
                        {answer.evaluation?.score || 0}/10
                      </span>
                    </>
                  )}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-5 w-5 text-gray-400 dark:text-gray-500 transition-transform ${
                      isExpanded ? "rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </button>

              {/* Expanded content */}
              {isExpanded && answer && (
                <div className="px-6 pb-6 border-t border-gray-100 dark:border-gray-700">
                  {/* Full question */}
                  <div className="mt-4 mb-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Question:</p>
                    <p className="text-gray-800 dark:text-gray-200">{question.question}</p>
                  </div>

                  {/* Your answer */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Your Answer:</p>
                    <p className="text-gray-700 dark:text-gray-300 italic">"{answer.transcription}"</p>
                  </div>

                  {/* Pronunciation breakdown */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Pronunciation:</p>
                    <div className="flex flex-wrap gap-1">
                      {answer.wordAnalysis.map((word, i) => (
                        <span
                          key={i}
                          className={`px-2 py-1 rounded text-xs ${getWordStyles(word.status)}`}
                        >
                          {word.word}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Answer evaluation */}
                  {answer.evaluation && (
                    <div className="space-y-3">
                      <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Relevance:</p>
                        <p className="text-gray-700 dark:text-gray-300 text-sm">{answer.evaluation.relevance}</p>
                      </div>

                      {answer.evaluation.strengths.length > 0 && (
                        <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
                          <p className="text-sm text-green-800 dark:text-green-300 font-medium mb-1">Strengths:</p>
                          <ul className="text-sm text-green-700 dark:text-green-400 space-y-1">
                            {answer.evaluation.strengths.map((s, i) => (
                              <li key={i}>- {s}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {answer.evaluation.improvements.length > 0 && (
                        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg">
                          <p className="text-sm text-yellow-800 dark:text-yellow-300 font-medium mb-1">
                            Areas to Improve:
                          </p>
                          <ul className="text-sm text-yellow-700 dark:text-yellow-400 space-y-1">
                            {answer.evaluation.improvements.map((s, i) => (
                              <li key={i}>- {s}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {answer.evaluation.sampleAnswer && (
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                          <p className="text-sm text-blue-800 dark:text-blue-300 font-medium mb-1">
                            Sample Answer:
                          </p>
                          <p className="text-sm text-blue-700 dark:text-blue-400">
                            {answer.evaluation.sampleAnswer}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Action buttons */}
      <div className="flex justify-center gap-4">
        <button
          onClick={onRestart}
          className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
        >
          Practice Again
        </button>
        <button
          onClick={onGoHome}
          className="px-6 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors"
        >
          Back to Home
        </button>
      </div>
    </div>
  );
};

export default InterviewResults;
